import { css } from '@compiled/react';
import type { FC } from 'react';
import React, { memo, useMemo, useState } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { EventHandlers } from '@atlaskit/editor-common/EventHandlers';
import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import type ProviderFactory from '@atlaskit/editor-common/provider-factory/provider-factory';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';
import { ValidationContextProvider } from '@atlaskit/renderer/validation-context';

import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';
import type { ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import type { LegacyContentMacroParams } from './LegacyContentMacroParams.ts';
import {
	NBMRendererHydrationClientEnhancer,
	type NBMRendererHydrationClientConfig,
} from './NBMRendererHydrationClientEnhancer/NBMRendererHydrationClientEnhancer.tsx';
import { NestedLcmHierarchyContext } from './NestedLcmHierarchyContext.ts';
import { useNestedLcmProviderFactory } from './useNestedLcmProviderFactory.ts';

export interface NBMRendererServerProps {
	contentId?: string;
	contentMode?: ADFRendererProps['contentMode'];
	node: ExtensionParams<LegacyContentMacroParams>;
}

export interface NBMRendererHydrationBridgeProps extends NBMRendererServerProps {
	clientAnnotationProvider?: ADFRendererProps['annotationProvider'];
	clientExtensionHandlers?: ADFRendererProps['extensionHandlers'];
	clientHydrationReady?: boolean;
	/** Handler set that produced the server markup; in effect until the client config lands. */
	extensionHandlers?: ADFRendererProps['extensionHandlers'];
}

export interface NBMRendererContentProps {
	allowAnnotations?: boolean;
	annotationProvider?: ADFRendererProps['annotationProvider'];
	appearance: 'full-width' | 'max';
	contentId?: string;
	contentMode?: ADFRendererProps['contentMode'];
	dataProviders?: ProviderFactory;
	adfDocument: DocNode;
	eventHandlers?: EventHandlers;
	extensionHandlers?: ADFRendererProps['extensionHandlers'];
	onSetLinkTarget?: (url: string) => '_blank' | undefined;
}

const DEFAULT_DOCUMENT: DocNode = {
	version: 1,
	type: 'doc',
	content: [
		{
			type: 'paragraph',
			content: [],
		},
	],
};

export const resolveNBMRenderConfig = (
	node: ExtensionParams<LegacyContentMacroParams>,
): Pick<NBMRendererContentProps, 'adfDocument' | 'appearance'> => ({
	adfDocument: node?.parameters?.nestedContent || DEFAULT_DOCUMENT,
	appearance: 'max',
});

const collapsedEmptyLists = css({
	// Hide bullets for deeply nested lists without sibling content so they look indented.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'& li:has(> ul:only-child), & li:has(> ol:only-child)': {
		listStyleType: 'none',
		paddingLeft: 0,
	},
});

const stableTableHydrationLayout = css({
	// Match the formatting boundary created when table overflow sentinels are added after hydration.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
	'&:has(.pm-table-wrapper)::before': {
		content: '""',
		display: 'table',
	},
});

export const NBMRendererContent: FC<NBMRendererContentProps> = ({
	allowAnnotations,
	annotationProvider,
	appearance,
	contentId,
	contentMode,
	dataProviders,
	adfDocument,
	eventHandlers,
	extensionHandlers,
	onSetLinkTarget,
}) => {
	const validationContextValue = useMemo(() => ({ skipValidation: true }), []);

	return (
		<ValidationContextProvider value={validationContextValue}>
			<RendererActionsContext>
				<div css={[collapsedEmptyLists, stableTableHydrationLayout]}>
					<ADFRenderer
						annotationProvider={annotationProvider}
						allowAnnotations={allowAnnotations}
						appearance={appearance}
						allowHeadingAnchorLinks={false}
						contentId={contentId}
						dataProviders={dataProviders}
						document={adfDocument}
						extensionHandlers={extensionHandlers}
						contentMode={contentMode}
						eventHandlers={eventHandlers}
						onSetLinkTarget={onSetLinkTarget}
					/>
				</div>
			</RendererActionsContext>
		</ValidationContextProvider>
	);
};

/**
 * Server/first-hydration renderer for the non-recursive LCM SSR path. It intentionally avoids the
 * client provider hooks because they can start queries that are unnecessary when no nested
 * extension is present, and it passes no nested handlers: without recursive SSR there is nothing
 * inside the LCM that the SSR handler set would own.
 */
export const NBMRendererServer: FC<NBMRendererServerProps> = memo(
	({ contentId, contentMode, node }) => {
		const { adfDocument, appearance } = resolveNBMRenderConfig(node);

		return (
			<NBMRendererContent
				adfDocument={adfDocument}
				appearance={appearance}
				contentId={contentId}
				contentMode={contentMode}
			/>
		);
	},
);

/**
 * Keeps the nested ADFRenderer mounted across the SSR-handler to manifest-ready handoff. The first
 * client render uses exactly the server configuration; client-only providers are attached in place
 * only after both Confluence hydration and the LCM manifest preload have completed.
 *
 * `dataProviders` is deliberately NOT deferred with the rest of the client config. Nested extensions
 * reach their manifests through the factory's `extensionProvider`, and platform's `WithProviders`
 * subscribes in its constructor without ever re-subscribing, so a factory attached after mount can
 * never reach the extensions already rendered — every `null`-returning handler would be left with a
 * dead fall-through and no way to reach the manifest. The factory is therefore supplied from the
 * very first render, on the server and on the client alike, so both sides agree.
 */
export const NBMRendererHydrationBridge: FC<NBMRendererHydrationBridgeProps> = memo(
	({
		clientAnnotationProvider,
		clientExtensionHandlers,
		clientHydrationReady,
		contentId,
		contentMode,
		extensionHandlers,
		node,
	}) => {
		const renderServerPlaceholder = useRenderServerPlaceholder();
		const [clientConfig, setClientConfig] = useState<NBMRendererHydrationClientConfig>();
		const { adfDocument, appearance } = resolveNBMRenderConfig(node);
		const dataProviders = useNestedLcmProviderFactory();
		const shouldLoadClientConfig = clientHydrationReady && !renderServerPlaceholder;

		return (
			<NestedLcmHierarchyContext.Provider value>
				<NBMRendererContent
					adfDocument={adfDocument}
					allowAnnotations={clientConfig?.allowAnnotations}
					annotationProvider={clientConfig?.annotationProvider}
					appearance={appearance}
					contentId={contentId}
					contentMode={contentMode}
					dataProviders={dataProviders}
					eventHandlers={clientConfig?.eventHandlers}
					extensionHandlers={clientConfig ? clientExtensionHandlers : extensionHandlers}
					onSetLinkTarget={clientConfig?.onSetLinkTarget}
				/>
				{shouldLoadClientConfig ? (
					<NBMRendererHydrationClientEnhancer
						adfDocument={adfDocument}
						annotationProvider={clientAnnotationProvider}
						contentId={contentId}
						onLoad={setClientConfig}
					/>
				) : null}
			</NestedLcmHierarchyContext.Provider>
		);
	},
);
